import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Box,
  Typography,
  Chip,
  Skeleton,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import reportService from '../services/reportService';
import { useLanguage } from '../contexts/LanguageContext';

const NOMBRES_MES_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const NOMBRES_MES_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const COLOR_POSITIVO = '#2e7d32';
const COLOR_NEGATIVO = '#c62828';

// 28 ago 2026 (pedido de Augusto): tendencia automática de ahorro y deuda
// en el Panel -- explícitamente SIN metas manuales que el cliente tenga
// que configurar (decisión tomada con él). Solo lee el historial real de
// los últimos 6 meses (ver /reports/trend en el backend) y dice, en una
// frase, si va mejorando o empeorando. Card aparte de NotionDashboard.js
// (que ya es un archivo grande) para no seguir engordándolo.
const TrendMiniChart = ({ datos, dataKey, nombreMes, simbolo }) => (
  <ResponsiveContainer width="100%" height={110}>
    <BarChart data={datos} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
      <YAxis hide />
      <ReferenceLine y={0} stroke="#bbb" />
      <Tooltip
        formatter={(value) => [`${simbolo} ${Number(value).toLocaleString('es-PE', { maximumFractionDigits: 0 })}`, '']}
        labelFormatter={(label) => label}
      />
      <Bar dataKey={dataKey} radius={[3, 3, 0, 0]}>
        {datos.map((d, idx) => (
          <Cell key={idx} fill={d[dataKey] >= 0 ? COLOR_POSITIVO : COLOR_NEGATIVO} />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const TendenciaChip = ({ mejorando, empeorando, textoMejor, textoPeor, textoEstable }) => {
  if (mejorando) {
    return <Chip size="small" icon={<TrendingUpIcon />} label={textoMejor} sx={{ bgcolor: '#e8f5e9', color: COLOR_POSITIVO, fontWeight: 600 }} />;
  }
  if (empeorando) {
    return <Chip size="small" icon={<TrendingDownIcon />} label={textoPeor} sx={{ bgcolor: '#ffebee', color: COLOR_NEGATIVO, fontWeight: 600 }} />;
  }
  return <Chip size="small" icon={<TrendingFlatIcon />} label={textoEstable} sx={{ bgcolor: 'grey.100', color: 'text.secondary', fontWeight: 600 }} />;
};

const SavingsDebtTrend = () => {
  const { language } = useLanguage();
  const isEs = language === 'es';
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    reportService
      .getTrend(6)
      .then(({ data }) => {
        if (!cancelled) setTrend(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardHeader title={isEs ? 'Tendencia de ahorro y deuda' : 'Savings & debt trend'} />
        <Divider />
        <CardContent>
          <Skeleton variant="rectangular" height={110} />
        </CardContent>
      </Card>
    );
  }

  // Si falla la carga, o el cliente todavía no tiene suficiente historial
  // (menos de 2 meses con datos), no tiene sentido mostrar una tendencia
  // -- se omite la tarjeta entera en vez de mostrar un gráfico vacío o
  // engañoso con un solo punto.
  const mesesConDatos = trend?.meses?.filter((m) => m.ahorro_neto !== 0 || m.movimiento_neto_deuda !== 0) || [];
  if (error || !trend || mesesConDatos.length < 2) return null;

  const nombresMes = isEs ? NOMBRES_MES_ES : NOMBRES_MES_EN;
  const datosAhorro = trend.meses.map((m) => ({ etiqueta: nombresMes[m.month - 1], ahorro_neto: m.ahorro_neto }));
  const datosDeuda = trend.meses.map((m) => ({ etiqueta: nombresMes[m.month - 1], movimiento_neto_deuda: m.movimiento_neto_deuda }));

  // Tendencia: promedio de la segunda mitad de los meses disponibles vs.
  // la primera mitad -- más estable que solo comparar el último mes
  // contra el anterior (un solo mes suelto puede ser ruido).
  const promedio = (arr, key) => (arr.length ? arr.reduce((acc, m) => acc + m[key], 0) / arr.length : 0);
  const mitad = Math.floor(trend.meses.length / 2) || 1;
  const primeraMitad = trend.meses.slice(0, mitad);
  const segundaMitad = trend.meses.slice(mitad);

  const ahorroAntes = promedio(primeraMitad, 'ahorro_neto');
  const ahorroDespues = promedio(segundaMitad, 'ahorro_neto');
  const ahorroMejorando = ahorroDespues > ahorroAntes * 1.05;
  const ahorroEmpeorando = ahorroDespues < ahorroAntes * 0.95;

  const deudaAntes = promedio(primeraMitad, 'movimiento_neto_deuda');
  const deudaDespues = promedio(segundaMitad, 'movimiento_neto_deuda');
  const deudaMejorando = deudaDespues > deudaAntes + 10;
  const deudaEmpeorando = deudaDespues < deudaAntes - 10;

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title={isEs ? 'Tendencia de ahorro y deuda' : 'Savings & debt trend'}
        subheader={isEs ? `Últimos ${mesesConDatos.length} meses con movimientos, calculado solo con tu historial real` : `Last ${mesesConDatos.length} months with activity, calculated only from your real history`}
      />
      <Divider />
      <CardContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">{isEs ? 'Ahorro neto mensual' : 'Monthly net savings'}</Typography>
              <TendenciaChip
                mejorando={ahorroMejorando}
                empeorando={ahorroEmpeorando}
                textoMejor={isEs ? 'Mejorando' : 'Improving'}
                textoPeor={isEs ? 'Empeorando' : 'Worsening'}
                textoEstable={isEs ? 'Estable' : 'Stable'}
              />
            </Box>
            <TrendMiniChart datos={datosAhorro} dataKey="ahorro_neto" simbolo="S/" />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">{isEs ? 'Movimiento neto de deuda' : 'Net debt movement'}</Typography>
              <TendenciaChip
                mejorando={deudaMejorando}
                empeorando={deudaEmpeorando}
                textoMejor={isEs ? 'Bajando' : 'Decreasing'}
                textoPeor={isEs ? 'Subiendo' : 'Increasing'}
                textoEstable={isEs ? 'Estable' : 'Stable'}
              />
            </Box>
            <TrendMiniChart datos={datosDeuda} dataKey="movimiento_neto_deuda" simbolo="S/" />
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {isEs
            ? 'Verde = mes a tu favor (ahorraste, o pagaste más deuda de la que generaste). Rojo = mes en contra.'
            : 'Green = month in your favor (you saved, or paid down more debt than you generated). Red = month against you.'}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default SavingsDebtTrend;
